import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  GATEWAY_BILLET_REQUESTS_MAX,
  GatewayBilletRequest,
  ItauGatewayCertificateRequest,
  ItauGatewayConfig,
  readBilletRequests,
  readConfig,
  readConfigList,
  readPdfResult,
  toSavePayload,
} from '../models/itau-gateway-config';

/**
 * Itaú via gateway HubFloripa (/secure/bank-gateway/itau/*).
 * Tela "Itau Bank API": configs da marina, uma por carteira de cobrança; as escritas devolvem a config
 * atualizada em `data.config` (ou null se não vier). Tela de faturas: situação do pedido de boleto e
 * nova tentativa do PDF. Tenant e usuário vão nos headers do interceptor (MarinaId/UserId).
 * Recusas de regra e do gateway chegam como 404 (gateway desligado ou credencial recusada = 503),
 * com a mensagem em data.exception.
 */
@Injectable({
  providedIn: 'root'
})
export class ItauBankGatewayService {

  static readonly ROOT_PATH = '/secure/bank-gateway/itau';
  static readonly BASE_PATH = ItauBankGatewayService.ROOT_PATH + '/config';

  constructor(private http: HttpClient) {
  }

  private url(suffix: string = ''): string {
    return environment.apiHost + ItauBankGatewayService.BASE_PATH + suffix;
  }

  private rootUrl(suffix: string): string {
    return environment.apiHost + ItauBankGatewayService.ROOT_PATH + suffix;
  }

  listConfigs(): Observable<ItauGatewayConfig[]> {
    return this.http.get(this.url()).pipe(map((response: any) => readConfigList(response)));
  }

  saveConfig(config: ItauGatewayConfig): Observable<ItauGatewayConfig | null> {
    return this.http.post(this.url(), toSavePayload(config)).pipe(map((response: any) => readConfig(response)));
  }

  generateCertificate(id: number, request: ItauGatewayCertificateRequest): Observable<ItauGatewayConfig | null> {
    const body: ItauGatewayCertificateRequest = {
      clientId: (request.clientId || '').trim(),
      tokenTemporario: (request.tokenTemporario || '').trim(),
    };
    if (request.substituir) {
      body.substituir = true;
    }
    return this.http.post(this.url(`/${id}/certificate`), body).pipe(map((response: any) => readConfig(response)));
  }

  testConnection(id: number): Observable<ItauGatewayConfig | null> {
    return this.http.post(this.url(`/${id}/test`), {}).pipe(map((response: any) => readConfig(response)));
  }

  activate(id: number): Observable<ItauGatewayConfig | null> {
    return this.http.post(this.url(`/${id}/activate`), {}).pipe(map((response: any) => readConfig(response)));
  }

  deactivate(id: number): Observable<ItauGatewayConfig | null> {
    return this.http.post(this.url(`/${id}/deactivate`), {}).pipe(map((response: any) => readConfig(response)));
  }

  /**
   * Situação do pedido de boleto das faturas (ids repetidos e nulos saem; no máximo 500).
   * Lista vazia não chama o backend.
   */
  findBilletRequests(invoiceIds: number[]): Observable<GatewayBilletRequest[]> {
    const ids = Array.from(new Set((invoiceIds || []).filter((id) => id != null)))
      .slice(0, GATEWAY_BILLET_REQUESTS_MAX);
    if (ids.length === 0) {
      return of([]);
    }
    const params = new HttpParams().set('invoiceIds', ids.join(','));
    return this.http.get(this.rootUrl('/billet-requests'), { params })
      .pipe(map((response: any) => readBilletRequests(response)));
  }

  /** Nova tentativa do PDF de um boleto emitido pelo gateway; true = PDF gravado. */
  retryBilletPdf(billetId: number): Observable<boolean> {
    return this.http.post(this.rootUrl(`/billet/${billetId}/pdf`), {})
      .pipe(map((response: any) => readPdfResult(response)));
  }
}
